import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard } from '@fortawesome/free-solid-svg-icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { operationType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { webURL } from '../../../../../services/API';
import { RootState } from '../../../../../redux/store';
import { ExpandedBlock } from '../../../expanded';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { AuthenticateSelect } from '../../../../auth/select';
import { Main, Styled } from './style';

type ShopifyTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

const DEFAULT_TOPIC = 'orders/create';
const CHECKOUTS_UPDATE_TOPIC = 'checkouts/update';
const CHECKOUT_ORDER_PHONE_READY_TOPIC = 'checkouts/order-phone-ready';

const topicOptions: { label: string; value: string }[] = [
  { label: 'Orders: Created', value: 'orders/create' },
  { label: 'Orders: Paid', value: 'orders/paid' },
  { label: 'Orders: Fulfilled', value: 'orders/fulfilled' },
  { label: 'Customers: Created', value: 'customers/create' },
  { label: 'Customers: Updated', value: 'customers/update' },
  { label: 'Products: Created', value: 'products/create' },
  { label: 'Products: Updated', value: 'products/update' },
  { label: 'Products: Deleted', value: 'products/delete' },
  { label: 'Checkouts: Created', value: 'checkouts/create' },
  { label: 'Checkouts: Updated', value: 'checkouts/update' },
  { label: 'Checkout: Order + Phone Ready', value: CHECKOUT_ORDER_PHONE_READY_TOPIC },
];

const supportedTopics = new Set(topicOptions.map(option => option.value));

type SyncState = 'idle' | 'pending' | 'syncing' | 'synced' | 'error';

function buildDefaultTestPayload(topic: string) {
  if (topic === CHECKOUT_ORDER_PHONE_READY_TOPIC) {
    return {
      id: Date.now(),
      customer: {
        last_order_id: 7509275148403,
      },
      shipping_lines: [
        {
          phone: '+201005307391',
        },
      ],
      source: 'manual-test',
      note: 'Compact trigger sends only order_id and phone',
    };
  }

  return {
    id: Date.now(),
    topic,
    source: 'manual-test',
    created_at: new Date().toISOString(),
    note: 'Edit this JSON before sending test event',
  };
}

function removeConflictingCheckoutTopic(topics: string[], nextTopic?: string) {
  if (nextTopic === CHECKOUT_ORDER_PHONE_READY_TOPIC) {
    return topics.filter(topic => topic !== CHECKOUTS_UPDATE_TOPIC);
  }
  if (nextTopic === CHECKOUTS_UPDATE_TOPIC) {
    return topics.filter(topic => topic !== CHECKOUT_ORDER_PHONE_READY_TOPIC);
  }
  if (topics.includes(CHECKOUT_ORDER_PHONE_READY_TOPIC) && topics.includes(CHECKOUTS_UPDATE_TOPIC)) {
    return topics.filter(topic => topic !== CHECKOUTS_UPDATE_TOPIC);
  }
  return topics;
}

function normalizeTopics(eventTopics: unknown, eventTopic?: string): string[] {
  const fromArray = Array.isArray(eventTopics)
    ? eventTopics
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(item => item.length > 0 && supportedTopics.has(item))
    : [];

  if (fromArray.length > 0) {
    return removeConflictingCheckoutTopic(Array.from(new Set(fromArray)));
  }

  const single = typeof eventTopic === 'string' ? eventTopic.trim() : '';
  if (single && supportedTopics.has(single)) {
    return [single];
  }

  return [];
}

export function ShopifyTrigger({ id, data, setChildMinimize, setChildSettings }: ShopifyTriggerProps) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const userToken = useSelector((state: RootState) => state.user.data?.token);
  const projectId = searchParams.get('id');

  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopy, setIsCopy] = React.useState<boolean>(false);
  const [isSecretCopy, setIsSecretCopy] = React.useState<boolean>(false);
  const [link, setLink] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [syncState, setSyncState] = React.useState<SyncState>('idle');
  const [syncError, setSyncError] = React.useState<string>('');
  const [lastSyncedAt, setLastSyncedAt] = React.useState<number | null>(null);
  const [isTopicSectionOpen, setIsTopicSectionOpen] = React.useState<boolean>(true);
  const [isWebhookUrlSectionOpen, setIsWebhookUrlSectionOpen] = React.useState<boolean>(false);
  const [testTopic, setTestTopic] = React.useState<string>(DEFAULT_TOPIC);
  const [testPayloadText, setTestPayloadText] = React.useState<string>(
    JSON.stringify(buildDefaultTestPayload(DEFAULT_TOPIC), null, 2)
  );
  const [isSendingTestEvent, setIsSendingTestEvent] = React.useState<boolean>(false);
  const [testEventError, setTestEventError] = React.useState<string>('');
  const [testEventSuccess, setTestEventSuccess] = React.useState<string>('');
  const backgroundSyncTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const syncRequestIdRef = React.useRef(0);

  const selectedTopics = React.useMemo(
    () => normalizeTopics(data.value?.eventTopics, data.value?.eventTopic),
    [data.value?.eventTopics, data.value?.eventTopic]
  );

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
    if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    const nextTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    if (nextTopics.length === 0) {
      updateNodeValue(id, setNodes, { eventTopics: [DEFAULT_TOPIC], eventTopic: DEFAULT_TOPIC });
      return;
    }

    if (!Array.isArray(data.value?.eventTopics)) {
      updateNodeValue(id, setNodes, { eventTopics: nextTopics, eventTopic: nextTopics[0] });
      return;
    }

    const currentTopics = (data.value.eventTopics as unknown[])
      .filter((item: unknown): item is string => typeof item === 'string')
      .map((item: string) => item.trim());
    const isSame =
      currentTopics.length === nextTopics.length &&
      currentTopics.every((item: string, index: number) => item === nextTopics[index]);

    if (!isSame || data.value?.eventTopic !== nextTopics[0]) {
      updateNodeValue(id, setNodes, { eventTopics: nextTopics, eventTopic: nextTopics[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.eventTopics, data.value?.eventTopic]);

  React.useEffect(() => {
    if (data?.triggerCredentials?.webhookUrl) {
      setLink(data.triggerCredentials.webhookUrl);
      setError('');
    }
  }, [data?.triggerCredentials?.webhookUrl]);

  React.useEffect(() => () => {
    if (backgroundSyncTimeoutRef.current) {
      clearTimeout(backgroundSyncTimeoutRef.current);
    }
    syncRequestIdRef.current += 1;
  }, []);

  React.useEffect(() => {
    const preferredTopic = selectedTopics[0] || DEFAULT_TOPIC;
    if (!supportedTopics.has(testTopic) || (selectedTopics.length > 0 && !selectedTopics.includes(testTopic))) {
      setTestTopic(preferredTopic);
      setTestPayloadText(JSON.stringify(buildDefaultTestPayload(preferredTopic), null, 2));
    }
  }, [selectedTopics, testTopic]);

  const syncStatusText = React.useMemo(() => {
    if (syncState === 'pending') return 'Saving topic changes...';
    if (syncState === 'syncing') return 'Syncing in background...';
    if (syncState === 'error') return 'Background sync failed';
    if (syncState === 'synced') return 'Topics synced';
    if (!link) return 'Webhook not generated yet';
    if (!data.value?.connection?.id || selectedTopics.length === 0) return 'Configure connection + topics';
    return 'Ready';
  }, [syncState, link, data.value?.connection?.id, selectedTopics.length]);

  const applyWebhookResponse = React.useCallback((responseData: any, payload: {
    eventTopics: string[];
    eventTopic: string;
    webhookClientSecret: string;
  }) => {
    const { nodeId, webhookUrl, blockId } = responseData || {};
    const nextWebhookUrl = webhookUrl || link;

    setNodes(prev =>
      prev.map(node =>
        node.data.index === data.index
          ? {
              ...node,
              data: {
                ...node.data,
                value: {
                  ...node.data.value,
                  eventTopics: payload.eventTopics,
                  eventTopic: payload.eventTopic,
                  webhookClientSecret: payload.webhookClientSecret,
                },
                triggerCredentials: nodeId && nextWebhookUrl && blockId
                  ? { nodeId, webhookUrl: nextWebhookUrl, blockId }
                  : node.data.triggerCredentials,
              },
            }
          : node
      )
    );

    if (nextWebhookUrl) {
      setLink(nextWebhookUrl);
    }
  }, [data.index, link, setNodes]);

  const syncWebhook = React.useCallback(async (params: {
    eventTopics: string[];
    connectionId: string;
    webhookClientSecret: string;
    endpoint: 'activate' | 'generate-link';
    background: boolean;
  }) => {
    const { eventTopics, connectionId, webhookClientSecret, endpoint, background } = params;
    if (!projectId) throw new Error('Missing project id');
    if (!userToken) throw new Error('Missing auth token');
    if (!connectionId) throw new Error('Please select a Shopify connection first');
    if (!eventTopics.length) throw new Error('Please select at least one event topic');

    const eventTopic = eventTopics[0];
    const requestId = ++syncRequestIdRef.current;
    setSyncError('');

    if (background) {
      setSyncState('syncing');
    } else {
      setIsLoading(true);
    }

    try {
      const response = await axios.post(
        `${webURL}/triggers/shopify/${endpoint}`,
        {
          nodeIndex: data.index,
          projectId,
          eventTopic,
          eventTopics,
          connectionId,
          webhookClientSecret,
        },
        {
          headers: {
            'x-auth-token': userToken,
          },
        }
      );

      if (requestId !== syncRequestIdRef.current) return;

      applyWebhookResponse(response.data, {
        eventTopics,
        eventTopic,
        webhookClientSecret,
      });
      setError('');

      setSyncState('synced');
      setLastSyncedAt(Date.now());
    } catch (err: any) {
      if (requestId !== syncRequestIdRef.current) return;

      const message = err.response?.data?.message || err.message || 'Failed to generate webhook URL';
      const normalizedMessage = message.includes('Trigger node does not exist')
        ? 'Please SAVE the project first (Ctrl+S), then reopen this panel.'
        : message;

      if (background) {
        setSyncState('error');
        setSyncError(normalizedMessage);
      } else {
        setError(normalizedMessage);
        throw err;
      }
    } finally {
      if (requestId !== syncRequestIdRef.current) return;
      if (background) return;
      setIsLoading(false);
    }
  }, [applyWebhookResponse, data.index, projectId, userToken]);

  const queueBackgroundSync = React.useCallback((topics: string[], delayMs = 700, overrides?: {
    connectionId?: string;
    webhookClientSecret?: string;
  }) => {
    const connectionId = overrides?.connectionId || data.value?.connection?.id || '';
    const webhookClientSecret = overrides?.webhookClientSecret ?? (data.value?.webhookClientSecret?.trim() || '');
    if (!projectId || !userToken || !connectionId || topics.length === 0) return;

    if (backgroundSyncTimeoutRef.current) {
      clearTimeout(backgroundSyncTimeoutRef.current);
    }

    setSyncState('pending');
    setSyncError('');

    backgroundSyncTimeoutRef.current = setTimeout(() => {
      backgroundSyncTimeoutRef.current = null;
      void syncWebhook({
        eventTopics: topics,
        connectionId,
        webhookClientSecret,
        endpoint: 'activate',
        background: true,
      });
    }, delayMs);
  }, [data.value?.connection?.id, data.value?.webhookClientSecret, projectId, syncWebhook, userToken]);

  React.useEffect(() => {
    if (!isExpanded || link || !data.value?.connection?.id || selectedTopics.length === 0) return;
    queueBackgroundSync(selectedTopics, 500);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, selectedTopics.join('|')]);

  const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
    if (type === 'add') {
      updateNodeValue(id, setNodes, { connection: connectionData });
      if (selectedTopics.length > 0) {
        queueBackgroundSync(selectedTopics, 400, {
          connectionId: connectionData?.id,
          webhookClientSecret: data.value?.webhookClientSecret?.trim() || '',
        });
      }
    } else {
      updateNodeValue(id, setNodes, { connection: null });
      setSyncState('idle');
      setSyncError('');
    }
  };

  const handleToggleTopic = (topic: string) => {
    if (!supportedTopics.has(topic)) return;
    const currentTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    const nextTopics = currentTopics.includes(topic)
      ? currentTopics.filter(item => item !== topic)
      : removeConflictingCheckoutTopic([...currentTopics, topic], topic);

    updateNodeValue(id, setNodes, {
      eventTopics: nextTopics,
      eventTopic: nextTopics[0] || '',
    });
    if (nextTopics.length > 0) {
      queueBackgroundSync(nextTopics);
    } else {
      setSyncState('idle');
      setSyncError('');
    }
  };

  const handleWebhookSecretChange = (value: string) => {
    updateNodeValue(id, setNodes, { webhookClientSecret: value.trim() });
  };

  const handleTestTopicChange = (topic: string) => {
    if (!supportedTopics.has(topic)) return;
    setTestTopic(topic);
    setTestPayloadText(JSON.stringify(buildDefaultTestPayload(topic), null, 2));
    setTestEventError('');
    setTestEventSuccess('');
  };

  const handleToggleTopicsSection = () => {
    setIsTopicSectionOpen(prev => {
      const next = !prev;
      if (next) {
        setIsWebhookUrlSectionOpen(false);
      }
      return next;
    });
  };

  const handleToggleWebhookUrlSection = () => {
    setIsWebhookUrlSectionOpen(prev => {
      const next = !prev;
      if (next) {
        setIsTopicSectionOpen(false);
      }
      return next;
    });
  };

  const generateLink = async () => {
    try {
      const eventTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
      const connectionId = data.value?.connection?.id;
      const webhookClientSecret = data.value?.webhookClientSecret?.trim() || '';
      await syncWebhook({
        eventTopics,
        connectionId,
        webhookClientSecret,
        endpoint: 'generate-link',
        background: false,
      });
    } catch (err: any) {
      console.error('Error generating Shopify webhook URL:', err);
    }
  };

  const sendTestEvent = async () => {
    if (!projectId) {
      setTestEventError('Missing project id');
      return;
    }
    if (!userToken) {
      setTestEventError('Missing auth token');
      return;
    }
    if (!testTopic) {
      setTestEventError('Choose a topic first');
      return;
    }

    let parsedPayload: Record<string, any>;
    try {
      const candidate = JSON.parse(testPayloadText);
      if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) {
        throw new Error('Payload must be a JSON object');
      }
      parsedPayload = candidate as Record<string, any>;
    } catch (parseError: any) {
      setTestEventError(parseError?.message || 'Invalid JSON payload');
      setTestEventSuccess('');
      return;
    }

    setIsSendingTestEvent(true);
    setTestEventError('');
    setTestEventSuccess('');

    try {
      const response = await axios.post(
        `${webURL}/triggers/shopify/test-event`,
        {
          projectId,
          nodeIndex: data.index,
          topic: testTopic,
          payload: parsedPayload,
        },
        {
          headers: {
            'x-auth-token': userToken,
          },
        }
      );

      const eventId = response.data?.eventId || '';
      const receiptId = response.data?.receiptId || '';
      const message = receiptId ? `Test sent. Event: ${eventId} | Receipt: ${receiptId}` : `Test sent. Event: ${eventId}`;
      setTestEventSuccess(message);
    } catch (err: any) {
      const message = err.response?.data?.message || err.message || 'Failed to send test event';
      setTestEventError(message);
    } finally {
      setIsSendingTestEvent(false);
    }
  };

  const isWarning = !data.value?.connection || selectedTopics.length === 0;
  const hasSecret = !!data.value?.webhookClientSecret?.trim();
  const testTopicOptions = selectedTopics.length > 0 ? selectedTopics : [DEFAULT_TOPIC];

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '480px' }}
          >
            <Styled>
                <div className="action">
                  <AuthenticateSelect
                    type="shopify"
                    defaultId={data.value?.connection?.id}
                    handleAuthenticate={handleAuthenticate}
                    background={data.background}
                  />
                </div>

                <div className="topicSelect sectionCard">
                  <button
                    type="button"
                    className="sectionToggle"
                    onClick={handleToggleTopicsSection}
                  >
                    <span>Event Topics</span>
                    <span className="sectionToggleIcon">{isTopicSectionOpen ? '-' : '+'}</span>
                  </button>

                  {isTopicSectionOpen ? (
                    <div className="topicChecklist">
                      {topicOptions.map(option => {
                        const checked = selectedTopics.includes(option.value);
                        return (
                          <label key={option.value} className="topicItem">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleTopic(option.value)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="toolbar">
                  <button
                    className="generateBtn"
                    onClick={() => {
                      void generateLink();
                    }}
                    disabled={isLoading || !data.value?.connection || selectedTopics.length === 0}
                  >
                    {isLoading ? 'Generating...' : 'Generate Link'}
                  </button>
                  <div className={`syncState ${syncState}`}>
                    {syncState === 'syncing' ? <span className="syncSpinner" /> : <span className="syncDot" />}
                    <span>{syncStatusText}</span>
                  </div>
                </div>

                {isWarning ? <div className="warning">Select Shopify connection and at least one event topic to generate URL.</div> : null}
                {error ? <div className="error">{error}</div> : null}
                {syncError ? <div className="error">{syncError}</div> : null}
                {lastSyncedAt ? <div className="syncMeta">Last synced {new Date(lastSyncedAt).toLocaleTimeString()}</div> : null}

                <div className="webhook-section sectionCard">
                  <button
                    type="button"
                    className="sectionToggle"
                    onClick={handleToggleWebhookUrlSection}
                  >
                    <span>Shopify Webhook URL + Secret</span>
                    <span className="sectionToggleIcon">{isWebhookUrlSectionOpen ? '-' : '+'}</span>
                  </button>

                  {isWebhookUrlSectionOpen ? (
                    <div className="webhookPanelBody">
                      <div className="webhookGroup">
                        <div className="groupTitle">Shopify Webhook URL</div>
                        {link ? (
                          <CopyToClipboard
                            text={link}
                            onCopy={() => {
                              setIsCopy(true);
                              setTimeout(() => setIsCopy(false), 500);
                            }}
                          >
                            <div className="link-container" title={link}>
                              <div className="link-with-copy">
                                <FontAwesomeIcon
                                  icon={isCopy ? faCheck : faClipboard}
                                  className={`copy-icon ${isCopy ? 'copied' : ''}`}
                                />
                                <span className="link-text">{link}</span>
                              </div>
                            </div>
                          </CopyToClipboard>
                        ) : (
                          <div className="warning">Generate webhook URL first.</div>
                        )}
                      </div>

                      <div className="webhookGroup">
                        <div className="groupTitle">Webhook Signing Secret</div>
                        <div className="secret-hint">
                          Paste the value Shopify shows in this format:
                          {' '}
                          <strong>Your webhooks will be signed with ...</strong>
                        </div>
                        <input
                          className="secret-input"
                          type="text"
                          value={data.value?.webhookClientSecret || ''}
                          placeholder="12600c790d7f0d9e0f17e324c56604c857124cad3c3b48fae6171073519d5c40"
                          onChange={(event) => handleWebhookSecretChange(event.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />

                        {data.value?.webhookClientSecret ? (
                          <CopyToClipboard
                            text={data.value.webhookClientSecret}
                            onCopy={() => {
                              setIsSecretCopy(true);
                              setTimeout(() => setIsSecretCopy(false), 500);
                            }}
                          >
                            <div className="link-container" title={data.value.webhookClientSecret}>
                              <div className="link-with-copy">
                                <FontAwesomeIcon
                                  icon={isSecretCopy ? faCheck : faClipboard}
                                  className={`copy-icon ${isSecretCopy ? 'copied' : ''}`}
                                />
                                <span className="link-text">{data.value.webhookClientSecret}</span>
                              </div>
                            </div>
                          </CopyToClipboard>
                        ) : (
                          <div className="warning">Paste signing secret to verify Shopify HMAC.</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="collapsedValue">
                      <span className={link ? 'statusBadge ready' : 'statusBadge missing'}>
                        URL: {link ? 'Configured' : 'Missing'}
                      </span>
                      <span className={hasSecret ? 'statusBadge ready' : 'statusBadge missing'}>
                        Secret: {hasSecret ? 'Configured' : 'Missing'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="instructions-section">
                  <h5>Setup Instructions</h5>
                  <ol className="steps-list">
                    <li>Open Shopify Admin for your store.</li>
                    <li>Navigate to webhook settings for your app/store.</li>
                    <li>Select topic(s): <strong>{selectedTopics.length ? selectedTopics.join(', ') : DEFAULT_TOPIC}</strong>.</li>
                    <li>Paste the generated URL and save.</li>
                    <li>Trigger a real Shopify action to test end-to-end.</li>
                  </ol>
                </div>

                <div className="test-event sectionCard">
                  <div className="testHeader">Test Event (Manual)</div>
                  <div className="testBody">
                    <label className="testLabel" htmlFor={`${id}-test-topic`}>Topic</label>
                    <select
                      id={`${id}-test-topic`}
                      className="testSelect"
                      value={testTopic}
                      onChange={(event) => handleTestTopicChange(event.target.value)}
                    >
                      {testTopicOptions.map(topic => {
                        const option = topicOptions.find(item => item.value === topic);
                        return (
                          <option key={topic} value={topic}>
                            {option?.label || topic}
                          </option>
                        );
                      })}
                    </select>

                    <label className="testLabel" htmlFor={`${id}-test-payload`}>Payload (JSON object)</label>
                    <textarea
                      id={`${id}-test-payload`}
                      className="testPayload"
                      value={testPayloadText}
                      onChange={(event) => setTestPayloadText(event.target.value)}
                      spellCheck={false}
                    />

                    <button
                      type="button"
                      className="testBtn"
                      onClick={() => {
                        void sendTestEvent();
                      }}
                      disabled={isSendingTestEvent || selectedTopics.length === 0}
                    >
                      {isSendingTestEvent ? 'Sending...' : 'Send Test Event'}
                    </button>

                    {testEventError ? <div className="error testFeedback">{testEventError}</div> : null}
                    {testEventSuccess ? <div className="success testFeedback">{testEventSuccess}</div> : null}
                  </div>
                </div>
              </Styled>
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}
